import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, FileSpreadsheet, ArrowRight, ArrowLeft, Save } from "lucide-react";
import EspacioCombobox from "@/components/EspacioCombobox";
import ClienteCombobox from "@/components/ClienteCombobox";
import { toast } from "sonner";

const NINGUNA = "__none__";

const CAMPOS = [
  { k: "espacio_nombre", label: "Espacio (nombre del PDV)", req: true },
  { k: "importe", label: "Importe montaje (€)" },
  { k: "material_texto", label: "Material" },
  { k: "observaciones", label: "Observaciones" },
  { k: "referencia_cliente", label: "Ref. / nº pedido cliente" },
  { k: "direccion", label: "Dirección" },
  { k: "ciudad", label: "Municipio / Ciudad" },
  { k: "provincia", label: "Provincia" },
  { k: "cp", label: "Código postal" },
];

function parseNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(String(v).replace("€", "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function parseMedidasTexto(v) {
  const m = String(v || "").match(/(\d+[.,]?\d*)\s*[xX×]\s*(\d+[.,]?\d*)/);
  if (!m) return null;
  return [parseFloat(m[1].replace(",", ".")), parseFloat(m[2].replace(",", "."))];
}

export default function ImportMapeoDialog({ open, onOpenChange, onCreated }) {
  const [paso, setPaso] = useState(1);
  const [analisis, setAnalisis] = useState(null); // {columnas, filas, sugerencias, unidad_sugerida}
  const [subiendo, setSubiendo] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [fijos, setFijos] = useState({ cliente_id: "", nombre_peticion: "", marca: "", campana: "", fecha_objetivo: "" });
  const [mapeo, setMapeo] = useState({}); // campo -> idx columna (o null)
  const [modoMedidas, setModoMedidas] = useState("sin"); // sin | texto | columnas
  const [colMedidas, setColMedidas] = useState(null);
  const [colAncho, setColAncho] = useState(null);
  const [colAlto, setColAlto] = useState(null);
  const [unidad, setUnidad] = useState("mm");
  const [preview, setPreview] = useState(null); // {items, resumen}
  const [omitidos, setOmitidos] = useState({});
  const [nombrePlantilla, setNombrePlantilla] = useState("");
  const [importando, setImportando] = useState(false);
  const [espacios, setEspacios] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/clientes").then((r) => setClientes(r.data || [])).catch(() => {});
    api.get("/espacios").then((r) => setEspacios(r.data || [])).catch(() => {});
    api.get("/plantillas-mapeo-import").then((r) => setPlantillas(r.data || [])).catch(() => {});
    setPaso(1); setAnalisis(null); setPreview(null); setOmitidos({}); setNombrePlantilla("");
    setFijos({ cliente_id: "", nombre_peticion: "", marca: "", campana: "", fecha_objetivo: "" });
  }, [open]);

  const subirFichero = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/peticiones/import-mapeo/analizar", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setAnalisis(data);
      const s = data.sugerencias || {};
      const m = {};
      CAMPOS.forEach((c) => { m[c.k] = s[c.k] ?? null; });
      setMapeo(m);
      if (s.ancho != null && s.alto != null) {
        setModoMedidas("columnas"); setColAncho(s.ancho); setColAlto(s.alto);
      } else if (s.medidas_texto != null) {
        setModoMedidas("texto"); setColMedidas(s.medidas_texto);
      } else { setModoMedidas("sin"); }
      setUnidad(data.unidad_sugerida || "mm");
      setPaso(2);
      toast.success(`${data.n_filas} filas detectadas · ${data.columnas.length} columnas`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error leyendo el fichero");
    } finally { setSubiendo(false); }
  };

  const aplicarPlantilla = (p) => {
    if (!analisis) return;
    const idxDe = (nombreCol) => {
      if (!nombreCol) return null;
      const i = analisis.columnas.findIndex((c) => c.trim().toLowerCase() === String(nombreCol).trim().toLowerCase());
      return i >= 0 ? i : null;
    };
    const m = {};
    CAMPOS.forEach((c) => { m[c.k] = idxDe(p.mapeo?.[c.k]); });
    setMapeo(m);
    setModoMedidas(p.modo_medidas || "sin");
    setColMedidas(idxDe(p.mapeo?.medidas_texto_col));
    setColAncho(idxDe(p.mapeo?.ancho_col));
    setColAlto(idxDe(p.mapeo?.alto_col));
    setUnidad(p.unidad || "mm");
    if (p.cliente_id) setFijos((f) => ({ ...f, cliente_id: p.cliente_id }));
    toast.success(`Plantilla «${p.nombre}» aplicada`);
  };

  const construirItems = () => {
    const factor = unidad === "mm" ? 0.1 : 1;
    return analisis.filas.map((fila) => {
      const get = (k) => (mapeo[k] != null ? fila[mapeo[k]] : null);
      let ancho = null, alto = null, medidasTxt = null;
      if (modoMedidas === "columnas" && colAncho != null && colAlto != null) {
        const a = parseNum(fila[colAncho]); const b = parseNum(fila[colAlto]);
        if (a) ancho = Math.round(a * factor * 100) / 100;
        if (b) alto = Math.round(b * factor * 100) / 100;
      } else if (modoMedidas === "texto" && colMedidas != null) {
        medidasTxt = String(fila[colMedidas] || "").trim() || null;
        const par = parseMedidasTexto(medidasTxt);
        if (par) { ancho = Math.round(par[0] * factor * 100) / 100; alto = Math.round(par[1] * factor * 100) / 100; }
      }
      return {
        espacio_nombre: String(get("espacio_nombre") || "").trim(),
        direccion: String(get("direccion") || "").trim() || null,
        ciudad: String(get("ciudad") || "").trim() || null,
        provincia: String(get("provincia") || "").trim() || null,
        cp: String(get("cp") || "").trim() || null,
        importe: parseNum(get("importe")),
        material_texto: String(get("material_texto") || "").trim() || null,
        observaciones: String(get("observaciones") || "").trim() || null,
        referencia_cliente: String(get("referencia_cliente") || "").trim() || null,
        ancho_cm: ancho, alto_cm: alto, medidas_texto: medidasTxt,
      };
    }).filter((it) => it.espacio_nombre);
  };

  const previsualizar = async () => {
    if (mapeo.espacio_nombre == null) return toast.error("Mapea la columna del Espacio (obligatorio)");
    if (!fijos.cliente_id) return toast.error("Selecciona el cliente");
    if (!fijos.nombre_peticion.trim()) return toast.error("Indica el nombre de la petición");
    const items = construirItems();
    if (!items.length) return toast.error("Ninguna fila tiene valor en la columna de Espacio");
    try {
      const { data } = await api.post("/peticiones/import-mapeo/previsualizar", { items, cliente_id: fijos.cliente_id });
      setPreview(data); setOmitidos({}); setPaso(3);
    } catch (err) { toast.error(err.response?.data?.detail || "Error previsualizando"); }
  };

  const importar = async () => {
    setImportando(true);
    try {
      if (nombrePlantilla.trim()) {
        const nombreCol = (idx) => (idx != null ? analisis.columnas[idx] : null);
        const mapeoNombres = {};
        CAMPOS.forEach((c) => { mapeoNombres[c.k] = nombreCol(mapeo[c.k]); });
        mapeoNombres.medidas_texto_col = nombreCol(colMedidas);
        mapeoNombres.ancho_col = nombreCol(colAncho);
        mapeoNombres.alto_col = nombreCol(colAlto);
        await api.post("/plantillas-mapeo-import", {
          nombre: nombrePlantilla.trim(), cliente_id: fijos.cliente_id || null,
          mapeo: mapeoNombres, modo_medidas: modoMedidas, unidad,
        }).catch(() => toast.warning("No se pudo guardar la plantilla (la importación continúa)"));
      }
      const items = preview.items.map((it, i) => ({ ...it, _accion: omitidos[i] ? "omitir" : "importar" }));
      const { data } = await api.post("/peticiones/import-mapeo/commit", {
        cliente_id: fijos.cliente_id, nombre_peticion: fijos.nombre_peticion,
        marca: fijos.marca || null, campana: fijos.campana || null,
        fecha_objetivo: fijos.fecha_objetivo || null, items,
      });
      toast.success(`✅ ${data.peticion_codigo}: ${data.lineas_creadas} líneas · ${data.espacios_creados} espacios nuevos · ${data.espacios_reusados} reutilizados`, { duration: 10000 });
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error importando");
    } finally { setImportando(false); }
  };

  const SelectCol = ({ value, onChange, testid }) => (
    <Select value={value == null ? NINGUNA : String(value)} onValueChange={(v) => onChange(v === NINGUNA ? null : Number(v))}>
      <SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 h-8 text-xs">
        <SelectValue placeholder="— columna —" />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
        <SelectItem value={NINGUNA}>— sin mapear —</SelectItem>
        {analisis?.columnas.map((c, i) => (
          <SelectItem key={i} value={String(i)}>{c}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const muestraCol = (idx) => {
    if (idx == null || !analisis) return null;
    const vals = analisis.filas.slice(0, 2).map((f) => String(f[idx] ?? "").slice(0, 24)).filter(Boolean);
    return vals.join(" · ");
  };

  const totalActivos = useMemo(() => preview ? preview.items.filter((_, i) => !omitidos[i]).length : 0, [preview, omitidos]);

  const setItemEspacio = (i, esp) => {
    setPreview((pv) => ({
      ...pv,
      items: pv.items.map((it, j) => j === i
        ? { ...it, _espacio_match_id: esp ? esp.id : null, _espacio_match_nombre: esp ? esp.nombre : null }
        : it),
    }));
  };

  const nExistentes = useMemo(() => preview ? preview.items.filter((it, i) => !omitidos[i] && it._espacio_match_id).length : 0, [preview, omitidos]);
  const nNuevos = useMemo(() => preview ? preview.items.filter((it, i) => !omitidos[i] && !it._espacio_match_id).length : 0, [preview, omitidos]);
  const nSugerencias = useMemo(() => preview ? preview.items.filter((it) => !it._espacio_match_id && it._espacio_sugerido).length : 0, [preview]);

  const aceptarTodasSugerencias = () => {
    setPreview((pv) => ({
      ...pv,
      items: pv.items.map((it) => (!it._espacio_match_id && it._espacio_sugerido)
        ? { ...it, _espacio_match_id: it._espacio_sugerido.id, _espacio_match_nombre: it._espacio_sugerido.nombre }
        : it),
    }));
    toast.success(`${nSugerencias} sugerencia(s) aceptadas`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 w-[96vw] sm:w-auto max-w-[900px] max-h-[90vh] overflow-y-auto p-4"
        data-testid="import-mapeo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-cyan-400" /> Importar montajes con mapeo de columnas
            <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">solo montaje · paso {paso}/3</span>
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Crea una petición de solo montaje masiva desde cualquier Excel del cliente, eligiendo qué columna corresponde a cada campo.
          </DialogDescription>
        </DialogHeader>

        {paso === 1 && (
          <div className="space-y-3 py-4" data-testid="mapeo-paso1">
            <div className="text-sm text-zinc-400">Sube el fichero Excel <b>tal cual te lo manda el cliente</b> — no hace falta normalizarlo. En el siguiente paso elegirás qué columna corresponde a cada campo.</div>
            <label className={`block border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 rounded-xl p-10 text-center cursor-pointer transition-colors ${subiendo ? "opacity-50 pointer-events-none" : ""}`}>
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={subirFichero} data-testid="mapeo-file-input" />
              {subiendo ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-cyan-400" />
                : <><FileSpreadsheet className="w-8 h-8 mx-auto text-zinc-500 mb-2" /><div className="text-sm text-zinc-300">Haz clic para elegir el Excel (.xlsx)</div></>}
            </label>
            {plantillas.length > 0 && (
              <div className="text-[11px] text-zinc-500">💾 Tienes {plantillas.length} plantilla(s) de mapeo guardada(s) — podrás aplicarlas en el paso 2.</div>
            )}
          </div>
        )}

        {paso === 2 && analisis && (
          <div className="space-y-4" data-testid="mapeo-paso2">
            {plantillas.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap bg-zinc-900/50 border border-zinc-800 rounded p-2">
                <span className="text-[11px] text-zinc-400">Aplicar plantilla:</span>
                {plantillas.map((p) => (
                  <button key={p.id} onClick={() => aplicarPlantilla(p)}
                    data-testid={`plantilla-${p.id}`}
                    className="text-[11px] px-2 py-1 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/20">
                    💾 {p.nombre}
                  </button>
                ))}
              </div>
            )}
            <div>
              <div className="text-xs font-bold text-zinc-300 mb-2">1 · Datos fijos de la petición</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <ClienteCombobox
                  value={fijos.cliente_id}
                  onChange={(id) => setFijos((f) => ({ ...f, cliente_id: id || "" }))}
                  clientes={clientes}
                  placeholder="Cliente *"
                  testId="mapeo-cliente"
                  className="h-9 text-sm"
                />
                <Input placeholder="Nombre petición *" value={fijos.nombre_peticion} data-testid="mapeo-nombre"
                  onChange={(e) => setFijos((f) => ({ ...f, nombre_peticion: e.target.value }))} className="bg-zinc-900 border-zinc-800 h-9 text-sm" />
                <Input placeholder="Marca" value={fijos.marca} data-testid="mapeo-marca"
                  onChange={(e) => setFijos((f) => ({ ...f, marca: e.target.value }))} className="bg-zinc-900 border-zinc-800 h-9 text-sm" />
                <Input placeholder="Campaña" value={fijos.campana} data-testid="mapeo-campana"
                  onChange={(e) => setFijos((f) => ({ ...f, campana: e.target.value }))} className="bg-zinc-900 border-zinc-800 h-9 text-sm" />
                <Input type="date" title="Fecha objetivo" value={fijos.fecha_objetivo} data-testid="mapeo-fecha-objetivo"
                  onChange={(e) => setFijos((f) => ({ ...f, fecha_objetivo: e.target.value }))} className="bg-zinc-900 border-zinc-800 h-9 text-sm" />
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-zinc-300 mb-2">2 · Mapeo de columnas <span className="text-zinc-500 font-normal">({analisis.n_filas} filas detectadas)</span></div>
              <div className="space-y-1.5">
                {CAMPOS.map((c) => (
                  <div key={c.k} className="grid grid-cols-[180px_1fr_1fr] gap-2 items-center">
                    <div className={`text-xs ${c.req ? "text-zinc-100 font-bold" : "text-zinc-400"}`}>{c.label}{c.req && " *"}</div>
                    <SelectCol value={mapeo[c.k]} onChange={(v) => setMapeo((m) => ({ ...m, [c.k]: v }))} testid={`mapeo-col-${c.k}`} />
                    <div className="text-[10px] text-zinc-600 truncate">{muestraCol(mapeo[c.k]) || ""}</div>
                  </div>
                ))}
                <div className="grid grid-cols-[180px_1fr_1fr] gap-2 items-center pt-1 border-t border-zinc-800/60">
                  <div className="text-xs text-zinc-400">Medidas</div>
                  <Select value={modoMedidas} onValueChange={setModoMedidas}>
                    <SelectTrigger data-testid="mapeo-modo-medidas" className="bg-zinc-900 border-zinc-800 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      <SelectItem value="sin">Sin medidas</SelectItem>
                      <SelectItem value="texto">Columna única (ej. "620x460")</SelectItem>
                      <SelectItem value="columnas">Ancho + Alto separados</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={unidad} onValueChange={setUnidad}>
                    <SelectTrigger data-testid="mapeo-unidad" className="bg-zinc-900 border-zinc-800 h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      <SelectItem value="mm">mm → cm</SelectItem>
                      <SelectItem value="cm">cm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {modoMedidas === "texto" && (
                  <div className="grid grid-cols-[180px_1fr_1fr] gap-2 items-center">
                    <div className="text-xs text-zinc-500 pl-3">↳ Columna medidas</div>
                    <SelectCol value={colMedidas} onChange={setColMedidas} testid="mapeo-col-medidas" />
                    <div className="text-[10px] text-zinc-600 truncate">{muestraCol(colMedidas) || ""}</div>
                  </div>
                )}
                {modoMedidas === "columnas" && (
                  <>
                    <div className="grid grid-cols-[180px_1fr_1fr] gap-2 items-center">
                      <div className="text-xs text-zinc-500 pl-3">↳ Ancho</div>
                      <SelectCol value={colAncho} onChange={setColAncho} testid="mapeo-col-ancho" />
                      <div className="text-[10px] text-zinc-600 truncate">{muestraCol(colAncho) || ""}</div>
                    </div>
                    <div className="grid grid-cols-[180px_1fr_1fr] gap-2 items-center">
                      <div className="text-xs text-zinc-500 pl-3">↳ Alto</div>
                      <SelectCol value={colAlto} onChange={setColAlto} testid="mapeo-col-alto" />
                      <div className="text-[10px] text-zinc-600 truncate">{muestraCol(colAlto) || ""}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setPaso(1)} className="bg-transparent border-zinc-700 text-zinc-300">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Otro fichero
              </Button>
              <Button onClick={previsualizar} data-testid="mapeo-previsualizar-btn"
                className="bg-cyan-500 hover:bg-cyan-400 text-zinc-900 font-bold">
                Previsualizar <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {paso === 3 && preview && (
          <div className="space-y-3" data-testid="mapeo-paso3">
            <div className="flex items-center gap-2 flex-wrap text-[11px]">
              <span className="px-2 py-1 rounded bg-zinc-800 text-zinc-200">{totalActivos} de {preview.resumen.total} filas</span>
              <span className="px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">{nExistentes} espacios existentes</span>
              <span className="px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">{nNuevos} espacios nuevos</span>
              <span className="px-2 py-1 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">Σ importes: {preview.resumen.total_importe.toFixed(2)} €</span>
              {preview.resumen.auto_vinculados > 0 && (
                <span className="px-2 py-1 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/25" title="Vinculados automáticamente por CP/dirección/nombre">
                  🔗 {preview.resumen.auto_vinculados} auto
                </span>
              )}
              {nSugerencias > 0 && (
                <button onClick={aceptarTodasSugerencias} data-testid="mapeo-aceptar-todas-btn"
                  className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 font-bold">
                  ✓ Aceptar las {nSugerencias} sugerencias
                </button>
              )}
            </div>
            <div className="border border-zinc-800 rounded overflow-x-auto max-h-[42vh] overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-zinc-900 text-zinc-400 text-left">
                  <tr>
                    <th className="p-1.5">✓</th><th className="p-1.5">Espacio</th><th className="p-1.5">Medidas</th>
                    <th className="p-1.5">Material</th><th className="p-1.5">Importe</th><th className="p-1.5">Ref.</th><th className="p-1.5">Obs.</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((it, i) => (
                    <tr key={i} className={`border-t border-zinc-800/60 ${omitidos[i] ? "opacity-30" : ""}`}>
                      <td className="p-1.5">
                        <input type="checkbox" checked={!omitidos[i]} data-testid={`mapeo-fila-check-${i}`}
                          onChange={() => setOmitidos((o) => ({ ...o, [i]: !o[i] }))} />
                      </td>
                      <td className="p-1.5">
                        <div className="text-zinc-200">{it.espacio_nombre}</div>
                        {it._espacio_match_id ? (
                          <div className="text-[9px] text-emerald-400 flex items-center gap-1"
                            title={it._espacio_match_motivo ? `Vinculado por: ${it._espacio_match_motivo}` : ""}>
                            ✓ {it._espacio_match_nombre}
                            {String(it._espacio_match_motivo || "").startsWith("auto") && (
                              <span className="text-[8px] px-1 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 uppercase">auto</span>
                            )}
                            <button onClick={() => setItemEspacio(i, null)} title="Quitar vínculo (se creará como nuevo)"
                              data-testid={`mapeo-quitar-match-${i}`}
                              className="text-zinc-600 hover:text-red-400">✕</button>
                          </div>
                        ) : it._espacio_sugerido ? (
                          <div className="text-[9px] text-amber-300 flex items-center gap-1 flex-wrap"
                            title={it._espacio_sugerido.motivos?.length ? `Coincide: ${it._espacio_sugerido.motivos.join(" + ")}` : ""}>
                            ¿Es «{it._espacio_sugerido.nombre}»? ({Math.round(it._espacio_sugerido.score * 100)}%{it._espacio_sugerido.motivos?.length ? ` · ${it._espacio_sugerido.motivos.join("+")}` : ""})
                            <button onClick={() => setItemEspacio(i, it._espacio_sugerido)}
                              data-testid={`mapeo-aceptar-sug-${i}`}
                              className="px-1 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30">Sí</button>
                          </div>
                        ) : (
                          <div className="text-[9px] text-amber-400">+ nuevo{it.ciudad ? ` · ${it.ciudad}` : ""}</div>
                        )}
                        <div className="mt-0.5">
                          <EspacioCombobox
                            value={it._espacio_match_id || null}
                            onChange={(id) => setItemEspacio(i, id ? espacios.find((e) => e.id === id) : null)}
                            espacios={espacios}
                            clienteId={fijos.cliente_id || null}
                            allowNone
                            placeholder="🔍 buscar espacio…"
                            testId={`mapeo-buscar-espacio-${i}`}
                            className="h-6 text-[10px] max-w-[220px]"
                          />
                        </div>
                      </td>
                      <td className="p-1.5 text-zinc-300 whitespace-nowrap">{it.ancho_cm && it.alto_cm ? `${it.ancho_cm}×${it.alto_cm} cm` : (it.medidas_texto || "—")}</td>
                      <td className="p-1.5">
                        <div className="text-zinc-300">{it.material_texto || "—"}</div>
                        {it._material_match_nombre && <div className="text-[9px] text-emerald-400">→ {it._material_match_nombre}</div>}
                      </td>
                      <td className="p-1.5 text-zinc-200 font-mono whitespace-nowrap">{it.importe != null ? `${it.importe.toFixed(2)} €` : "—"}</td>
                      <td className="p-1.5 text-amber-200 font-mono whitespace-nowrap" data-testid={`mapeo-ref-${i}`}>{it.referencia_cliente || "—"}</td>
                      <td className="p-1.5 text-zinc-500 max-w-[180px] truncate" title={it.observaciones || ""}>{it.observaciones || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2">
              <Save className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
              <Input placeholder="Guardar mapeo como plantilla (opcional, ej. 'Excel Perfumerías D&G')"
                value={nombrePlantilla} onChange={(e) => setNombrePlantilla(e.target.value)}
                data-testid="mapeo-nombre-plantilla" className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setPaso(2)} className="bg-transparent border-zinc-700 text-zinc-300">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Volver al mapeo
              </Button>
              <Button onClick={importar} disabled={importando || totalActivos === 0} data-testid="mapeo-importar-btn"
                className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold">
                {importando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Importar {totalActivos} punto(s) de montaje
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
